import React from 'react';
import { useInfluencerStore } from '@/stores/influencerStore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { format } from 'date-fns';

export const PersonalDashboard: React.FC = () => {
  const { dashboardData } = useInfluencerStore();

  if (!dashboardData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No dashboard data available</p>
      </div>
    );
  }

  const { performanceChart, recentActivity } = dashboardData;

  return (
    <div className="space-y-6">
      {/* Performance Overview Chart */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Performance Overview</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={performanceChart}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tickFormatter={(value) => format(new Date(value), 'MMM dd')}
            />
            <YAxis />
            <Tooltip 
              labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
              formatter={(value: any, name: string) => [
                value, 
                name === 'clicks' ? 'Clicks' : name === 'signups' ? 'Signups' : 'Conversions'
              ]}
            />
            <Line type="monotone" dataKey="clicks" stroke="#3B82F6" strokeWidth={2} />
            <Line type="monotone" dataKey="signups" stroke="#10B981" strokeWidth={2} />
            <Line type="monotone" dataKey="conversions" stroke="#F59E0B" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Recent Activity */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {recentActivity.slice(0, 10).map((activity) => (
            <div key={activity.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  activity.status === 'paid' ? 'bg-green-500' :
                  activity.status === 'converted' ? 'bg-blue-500' :
                  activity.status === 'signed_up' ? 'bg-yellow-500' :
                  'bg-gray-400'
                }`} />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {activity.status === 'paid' ? 'Commission Paid' :
                     activity.status === 'converted' ? 'User Converted' :
                     activity.status === 'signed_up' ? 'User Signed Up' :
                     'Link Clicked'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {format(new Date(activity.createdAt), 'MMM dd, yyyy HH:mm')}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  ${activity.commissionEarned.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500">
                  {activity.referralCode}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-sm font-medium text-gray-500 mb-2">This Week</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Clicks:</span>
              <span className="text-sm font-medium">
                {performanceChart.slice(-7).reduce((sum, day) => sum + day.clicks, 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Signups:</span>
              <span className="text-sm font-medium">
                {performanceChart.slice(-7).reduce((sum, day) => sum + day.signups, 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Conversions:</span>
              <span className="text-sm font-medium">
                {performanceChart.slice(-7).reduce((sum, day) => sum + day.conversions, 0)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-sm font-medium text-gray-500 mb-2">This Month</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Clicks:</span>
              <span className="text-sm font-medium">
                {performanceChart.reduce((sum, day) => sum + day.clicks, 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Signups:</span>
              <span className="text-sm font-medium">
                {performanceChart.reduce((sum, day) => sum + day.signups, 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Conversions:</span>
              <span className="text-sm font-medium">
                {performanceChart.reduce((sum, day) => sum + day.conversions, 0)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Performance</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Avg. Daily Clicks:</span>
              <span className="text-sm font-medium">
                {(performanceChart.reduce((sum, day) => sum + day.clicks, 0) / performanceChart.length).toFixed(1)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Conversion Rate:</span>
              <span className="text-sm font-medium">
                {dashboardData.personalStats.conversionRate.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Earnings per Signup:</span>
              <span className="text-sm font-medium">
                ${(dashboardData.personalStats.totalEarnings / dashboardData.personalStats.totalSignups || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 